"use strict";
/**
 * Planning Module (Management Essentials) - Shared Types
 *
 * Zod schemas and TypeScript types for the Management module entities:
 * goals, work plans, meetings, action items, comments, roles, feed.
 *
 * DO NOT MOVE THIS FILE - it is referenced by management-service and platform-app.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.CreateFeedPostInputSchema = exports.FeedPostSchema = exports.UpdateRoleInputSchema = exports.CreateRoleInputSchema = exports.ManagementRoleSchema = exports.ManagementPermissionsSchema = exports.ManagementMemberSchema = exports.CreateCommentInputSchema = exports.CommentSchema = exports.CreateActionItemInputSchema = exports.ActionItemSchema = exports.UpdateMeetingInputSchema = exports.CreateMeetingInputSchema = exports.MeetingSchema = exports.AttendanceValueSchema = exports.UpdateWorkPlanInputSchema = exports.CreateWorkPlanInputSchema = exports.WorkPlanSchema = exports.UpdateNodeInputSchema = exports.CreateNodeInputSchema = exports.ManagementNodeSchema = exports.FeedPostTypeSchema = exports.ActionItemStatusSchema = exports.PlanStatusSchema = exports.PrioritySchema = exports.NodeStatusSchema = exports.NodeTypeSchema = void 0;
const zod_1 = require("zod");
// ========================
// Enums
// ========================
exports.NodeTypeSchema = zod_1.z.enum(['goal', 'sub_goal', 'feature', 'task']);
exports.NodeStatusSchema = zod_1.z.enum(['not_started', 'in_progress', 'blocked', 'complete']);
exports.PrioritySchema = zod_1.z.enum(['low', 'medium', 'high', 'urgent']);
exports.PlanStatusSchema = zod_1.z.enum(['draft', 'active', 'completed']);
exports.ActionItemStatusSchema = zod_1.z.enum(['open', 'complete']);
exports.FeedPostTypeSchema = zod_1.z.enum(['update', 'system']);
// ========================
// ManagementNode
// ========================
exports.ManagementNodeSchema = zod_1.z.object({
    id: zod_1.z.string().min(1),
    tenantId: zod_1.z.string().min(1),
    parentId: zod_1.z.string().nullable(),
    goalId: zod_1.z.string().nullable(),
    type: exports.NodeTypeSchema,
    title: zod_1.z.string().min(1),
    description: zod_1.z.string().nullable(),
    startDate: zod_1.z.string().nullable(),
    targetDate: zod_1.z.string().nullable(),
    completedDate: zod_1.z.string().nullable(),
    status: exports.NodeStatusSchema,
    blockReason: zod_1.z.string().nullable(),
    priority: exports.PrioritySchema,
    progress: zod_1.z.number().min(0).max(100),
    assigneeIds: zod_1.z.array(zod_1.z.string()),
    createdBy: zod_1.z.string().min(1),
    depth: zod_1.z.number().int().min(0),
    childCount: zod_1.z.number().int().min(0),
    sortOrder: zod_1.z.number().int().min(0),
    createdAt: zod_1.z.string(),
    updatedAt: zod_1.z.string(),
});
exports.CreateNodeInputSchema = zod_1.z.object({
    parentId: zod_1.z.string().nullable().optional(),
    goalId: zod_1.z.string().nullable().optional(),
    type: exports.NodeTypeSchema,
    title: zod_1.z.string().min(1),
    description: zod_1.z.string().nullable().optional(),
    startDate: zod_1.z.string().nullable().optional(),
    targetDate: zod_1.z.string().nullable().optional(),
    status: exports.NodeStatusSchema.optional(),
    priority: exports.PrioritySchema.optional(),
    assigneeIds: zod_1.z.array(zod_1.z.string()).optional(),
    sortOrder: zod_1.z.number().int().min(0).optional(),
});
exports.UpdateNodeInputSchema = zod_1.z.object({
    parentId: zod_1.z.string().nullable().optional(),
    goalId: zod_1.z.string().nullable().optional(),
    type: exports.NodeTypeSchema.optional(),
    title: zod_1.z.string().min(1).optional(),
    description: zod_1.z.string().nullable().optional(),
    startDate: zod_1.z.string().nullable().optional(),
    targetDate: zod_1.z.string().nullable().optional(),
    completedDate: zod_1.z.string().nullable().optional(),
    status: exports.NodeStatusSchema.optional(),
    blockReason: zod_1.z.string().nullable().optional(),
    priority: exports.PrioritySchema.optional(),
    progress: zod_1.z.number().min(0).max(100).optional(),
    assigneeIds: zod_1.z.array(zod_1.z.string()).optional(),
    sortOrder: zod_1.z.number().int().min(0).optional(),
});
// ========================
// WorkPlan
// ========================
exports.WorkPlanSchema = zod_1.z.object({
    id: zod_1.z.string().min(1),
    tenantId: zod_1.z.string().min(1),
    name: zod_1.z.string().min(1),
    customLabel: zod_1.z.string().nullable(),
    startDate: zod_1.z.string(),
    endDate: zod_1.z.string(),
    status: exports.PlanStatusSchema,
    createdBy: zod_1.z.string().min(1),
    taskIds: zod_1.z.array(zod_1.z.string()),
    progress: zod_1.z.number().min(0).max(100),
    createdAt: zod_1.z.string(),
    updatedAt: zod_1.z.string(),
});
exports.CreateWorkPlanInputSchema = zod_1.z.object({
    name: zod_1.z.string().min(1),
    customLabel: zod_1.z.string().nullable().optional(),
    startDate: zod_1.z.string(),
    endDate: zod_1.z.string(),
    status: exports.PlanStatusSchema.optional(),
    taskIds: zod_1.z.array(zod_1.z.string()).optional(),
});
exports.UpdateWorkPlanInputSchema = zod_1.z.object({
    name: zod_1.z.string().min(1).optional(),
    customLabel: zod_1.z.string().nullable().optional(),
    startDate: zod_1.z.string().optional(),
    endDate: zod_1.z.string().optional(),
    status: exports.PlanStatusSchema.optional(),
    taskIds: zod_1.z.array(zod_1.z.string()).optional(),
    progress: zod_1.z.number().min(0).max(100).optional(),
});
// ========================
// Meeting
// ========================
exports.AttendanceValueSchema = zod_1.z.enum(['present', 'absent']);
exports.MeetingSchema = zod_1.z.object({
    id: zod_1.z.string().min(1),
    tenantId: zod_1.z.string().min(1),
    title: zod_1.z.string().min(1),
    description: zod_1.z.string().nullable(),
    date: zod_1.z.string(),
    duration: zod_1.z.number().int().positive(),
    linkedGoalId: zod_1.z.string().nullable(),
    attendeeIds: zod_1.z.array(zod_1.z.string()),
    attendance: zod_1.z.record(zod_1.z.string(), exports.AttendanceValueSchema),
    notes: zod_1.z.string(),
    createdBy: zod_1.z.string().min(1),
    createdAt: zod_1.z.string(),
    updatedAt: zod_1.z.string(),
});
exports.CreateMeetingInputSchema = zod_1.z.object({
    title: zod_1.z.string().min(1),
    description: zod_1.z.string().nullable().optional(),
    date: zod_1.z.string(),
    duration: zod_1.z.number().int().positive(),
    linkedGoalId: zod_1.z.string().nullable().optional(),
    attendeeIds: zod_1.z.array(zod_1.z.string()).optional(),
    notes: zod_1.z.string().optional(),
});
exports.UpdateMeetingInputSchema = zod_1.z.object({
    title: zod_1.z.string().min(1).optional(),
    description: zod_1.z.string().nullable().optional(),
    date: zod_1.z.string().optional(),
    duration: zod_1.z.number().int().positive().optional(),
    linkedGoalId: zod_1.z.string().nullable().optional(),
    attendeeIds: zod_1.z.array(zod_1.z.string()).optional(),
    attendance: zod_1.z.record(zod_1.z.string(), exports.AttendanceValueSchema).optional(),
    notes: zod_1.z.string().optional(),
});
// ========================
// ActionItem
// ========================
exports.ActionItemSchema = zod_1.z.object({
    id: zod_1.z.string().min(1),
    meetingId: zod_1.z.string().min(1),
    tenantId: zod_1.z.string().min(1),
    title: zod_1.z.string().min(1),
    assigneeId: zod_1.z.string().min(1),
    dueDate: zod_1.z.string().nullable(),
    status: exports.ActionItemStatusSchema,
    convertedToNodeId: zod_1.z.string().nullable(),
    createdAt: zod_1.z.string(),
    updatedAt: zod_1.z.string(),
});
exports.CreateActionItemInputSchema = zod_1.z.object({
    meetingId: zod_1.z.string().min(1),
    title: zod_1.z.string().min(1),
    assigneeId: zod_1.z.string().min(1),
    dueDate: zod_1.z.string().nullable().optional(),
});
// ========================
// Comment
// ========================
exports.CommentSchema = zod_1.z.object({
    id: zod_1.z.string().min(1),
    nodeId: zod_1.z.string().min(1),
    tenantId: zod_1.z.string().min(1),
    authorId: zod_1.z.string().min(1),
    body: zod_1.z.string().min(1),
    attachments: zod_1.z.array(zod_1.z.string()),
    mentions: zod_1.z.array(zod_1.z.string()),
    createdAt: zod_1.z.string(),
});
exports.CreateCommentInputSchema = zod_1.z.object({
    nodeId: zod_1.z.string().min(1),
    body: zod_1.z.string().min(1),
    attachments: zod_1.z.array(zod_1.z.string()).optional(),
    mentions: zod_1.z.array(zod_1.z.string()).optional(),
});
// ========================
// ManagementMember
// ========================
exports.ManagementMemberSchema = zod_1.z.object({
    tenantId: zod_1.z.string().min(1),
    userId: zod_1.z.string().min(1),
    roleId: zod_1.z.string().min(1),
    reportsTo: zod_1.z.string().nullable(),
    joinedAt: zod_1.z.string(),
});
// ========================
// ManagementPermissions & Role
// ========================
exports.ManagementPermissionsSchema = zod_1.z.object({
    goals: zod_1.z.array(zod_1.z.string()),
    workplans: zod_1.z.array(zod_1.z.string()),
    board: zod_1.z.array(zod_1.z.string()),
    tasks: zod_1.z.array(zod_1.z.string()),
    meetings: zod_1.z.array(zod_1.z.string()),
    feed: zod_1.z.array(zod_1.z.string()),
    team: zod_1.z.array(zod_1.z.string()),
    roles: zod_1.z.array(zod_1.z.string()),
    settings: zod_1.z.array(zod_1.z.string()),
});
exports.ManagementRoleSchema = zod_1.z.object({
    id: zod_1.z.string().min(1),
    tenantId: zod_1.z.string().min(1),
    name: zod_1.z.string().min(1),
    isSystem: zod_1.z.boolean(),
    permissions: exports.ManagementPermissionsSchema,
    createdAt: zod_1.z.string(),
    updatedAt: zod_1.z.string(),
});
exports.CreateRoleInputSchema = zod_1.z.object({
    name: zod_1.z.string().min(1),
    permissions: exports.ManagementPermissionsSchema,
});
exports.UpdateRoleInputSchema = zod_1.z.object({
    name: zod_1.z.string().min(1).optional(),
    permissions: exports.ManagementPermissionsSchema.optional(),
});
// ========================
// FeedPost
// ========================
exports.FeedPostSchema = zod_1.z.object({
    id: zod_1.z.string().min(1),
    tenantId: zod_1.z.string().min(1),
    authorId: zod_1.z.string().min(1),
    type: exports.FeedPostTypeSchema,
    body: zod_1.z.string().min(1),
    relatedNodeId: zod_1.z.string().nullable(),
    mentions: zod_1.z.array(zod_1.z.string()),
    createdAt: zod_1.z.string(),
});
exports.CreateFeedPostInputSchema = zod_1.z.object({
    type: exports.FeedPostTypeSchema,
    body: zod_1.z.string().min(1),
    relatedNodeId: zod_1.z.string().nullable().optional(),
    mentions: zod_1.z.array(zod_1.z.string()).optional(),
});
//# sourceMappingURL=management.js.map